/**
 * The Fuel Calculator's tank size (owner, 28 Sep 2026): the contract's OWN Ejar details (`fuelTankSize`, litres, of
 * the car on that contract), read through the engine (GET /api/store/contracts/{no}/fuel-tank) - never a per-model
 * table (the old typed table was wrong for Sonet: 51 L against Ejar's 45 L). Pure rules, tested alone.
 */
import { normalizePlate } from '../../../services/possessionMatcher';

/** What the calculator says when Ejar has no tank size for the car of a row: typed for this one calculation. */
export const NO_TANK_HINT = 'Ejar has no tank size for this car - type it for this calculation';

/** The tank as the calculator shows it when it came from Ejar. */
export const ejarTankText = (litres) => `Tank: ${litres} L (Ejar contract)`;

/**
 * The tank of one row of the contract's history from the engine's answer (`route`: { tankLitres, plate, error? },
 * or null when it could not be asked). Ejar's tank belongs to the car the details describe (`route.plate`): a
 * swapped contract's other car has another tank, so it gets none. Returns { litres, fromEjar } - litres null when
 * the tank must be typed (never saved anywhere).
 */
export const tankForRow = (route, rowPlate) => {
    const litres = Number(route && route.tankLitres);
    if (!route || route.error || !(litres > 0)) return { litres: null, fromEjar: false };
    const routePlate = normalizePlate(route.plate);
    if (!routePlate || routePlate !== normalizePlate(rowPlate)) return { litres: null, fromEjar: false };
    return { litres, fromEjar: true };
};

/** Said while the history's Ejar tanks are still being read (the tank may yet come from Ejar). */
export const TANK_READING_HINT = 'Reading the tank size from the Ejar contract...';

/**
 * An answer that says the engine or Ejar did not answer (`error`) is asked again after this long; any other answer
 * is kept for the page's life (Ejar having no tank for a car: the engine itself does not ask Ejar again for 24 h).
 * Before (review, 28 Sep 2026) one timed-out read left the tank to be typed until the page was reloaded.
 */
export const TANK_RETRY_MS = 2 * 60 * 1000;

/** Whether a kept tank read ({ at, failed, promise }) is used again at `now` (else the contract is asked again). */
export const keepTankAnswer = (entry, now) => Boolean(entry && entry.promise && !(entry.failed && now - entry.at >= TANK_RETRY_MS));

/**
 * The Ejar tanks of the contract's history rows: `rows` [{ id, contractNo, plate }], `answers` Map(CONTRACT NO ->
 * the engine's answer) -> { [row id]: litres } for the rows whose car is the one Ejar describes (tankForRow); a
 * row with none is typed.
 */
export const tanksOfHistory = (rows, answers) => {
    const out = {};
    (rows || []).forEach((row) => {
        const answer = answers && answers.get(String((row && row.contractNo) || '').trim().toUpperCase());
        const tank = tankForRow(answer || null, row && row.plate);
        if (tank.fromEjar) out[row.id] = tank.litres;
    });
    return out;
};
